import React, { useCallback, useEffect, useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import moment from "moment";
import { LuMoveRight } from "react-icons/lu";
import { TbClockStop } from "react-icons/tb";
import { Clock, MoveRight, Tag } from "lucide-react";
import {
  Badge,
  Divider,
  NumberFormatter,
  Tabs,
  Popover,
  Table,
  Skeleton,
  ScrollArea,
} from "@mantine/core";

import { IAirportDetails } from "types";
import { useUnsecure } from "libs/data-access";
import { EModal, EBadge, EPopover, EHighlightedText, ETable } from "libs/ui";
import {
  IFareRuleLongs,
  IFlightOnlineBookingList,
} from "libs/data-access/types";
import { getAirLineData, getTimeDifference, humanizeText } from "utils/utility";
import {
  getDuration,
  normalizeAkbarJourney,
} from "../hooks/utility/akbarAdapter";
import AkbarSsrDetails from "./AkbarSsrDetails";

const FlightDetails = ({
  flightTicket,
  areFareRulesToBeVisible,
  airportDetails,
}: {
  flightTicket: IFlightOnlineBookingList;
  areFareRulesToBeVisible: boolean;
  airportDetails: IAirportDetails[];
}) => {
  // const { postData } = useApi();
  const { getUnsecureData } = useUnsecure();

  const [showFareRules, { open: OpenFareRules, close: closeFareRules }] =
    useDisclosure(false);

  // const [airportDetails, setAirportDetails] = useState<IAirportDetails[]>([]);
  const [selectedFareRules, setSelectedFareRules] =
    useState<Record<string, IFareRuleLongs[]>>();
  const [myCharges, setMyCharges] = useState<{
    customerCancellationDomesticCharge: number | null;
    customerCancellationInternationalCharge: number | null;
    customerModificationDomesticCharge: number | null;
    customerModificationInternationalCharge: number | null;
  }>({
    customerCancellationDomesticCharge: null,
    customerCancellationInternationalCharge: null,
    customerModificationDomesticCharge: null,
    customerModificationInternationalCharge: null,
  });
  const [isMyChargeLoading, setIsMyChargeLoading] = useState<boolean>(false);

  // useEffect(() => {
  //   flightTicket.uuid && fetchAirportInformation();
  // }, [flightTicket]);

  useEffect(() => {
    if (showFareRules) {
      getMyCharges();
    }
  }, [showFareRules]);

  // API CALLS
  // const fetchAirportInformation = async () => {
  //   let allAirportCode: any[] = [];
  //   flightTicket?.bookingResponses?.forEach((trip) => {
  //     trip?.galileoBookingDetailResponses?.forEach((journey) => {
  //       journey?.flightBookingSegments?.forEach((segment) => {
  //         allAirportCode = [
  //           ...allAirportCode,
  //           segment.origin,
  //           segment.destination,
  //         ];
  //       });
  //     });
  //     trip?.navitaireBookingDetailResponses?.forEach((journey) => {
  //       journey?.navitaireBookingDetailSegments?.forEach((segment) => {
  //         allAirportCode = [
  //           ...allAirportCode,
  //           segment.origin,
  //           segment.destination,
  //         ];
  //       });
  //     });
  //   });

  //   const response = await postData(
  //     `unsecure/flight/one-g/get-airport-response?codes=${allAirportCode}`,
  //     null
  //   );
  //   if (response.isSuccess) {
  //     // console.log('Response:', response.data.data);
  //     setAirportDetails(response.data.data);
  //   } else {
  //     console.error(
  //       "Error occurred while requesting for Air Pricing:",
  //       response
  //     );
  //   }
  // };

  const getMyCharges = async () => {
    setIsMyChargeLoading(true);

    const response = await getUnsecureData(
      "common-content/normal-user/my-charge?travelServiceCategory=FLIGHT",
    );

    if (response.isSuccess) {
      // console.log("Response for my charges: ", response.data);
      setMyCharges(response.data);
    } else {
      // console.log("Error while searching my charges: ", response);
    }

    setIsMyChargeLoading(false);
  };

  // HANDLER FUNCTIONS
  function mapFareRulesToSegment(
    tripId: number,
    journeyKey: string, // origin_destination
    segment: any,
  ) {
    const galileoJourneys = flightTicket.bookingResponses.find(
      (booking) => booking.id === tripId,
    );

    const fareRuleList = galileoJourneys?.galileoBookingDetailResponses?.find(
      (each) => `${each.origin}_${each.destination}` === journeyKey,
    )?.bookingFareRules;

    const segmentWiseFareRuleList = fareRuleList?.find(
      (each) => each.fareInfoRef === segment.fareInfoRef,
    );

    setSelectedFareRules({
      [segment.fareInfoRef]: segmentWiseFareRuleList?.fareRuleLongs || [],
    });
  }
  console.log("Selected Flight: ", flightTicket);
  return (
    <div>
      <div>
        {/* GALILEO FLIGHT DETAILS */}
        {flightTicket?.bookingResponses?.map((trip) =>
          trip?.galileoBookingDetailResponses?.map((galBooking) => (
            <div
              key={flightTicket.uuid + "_flight_details"}
              className="mb-6 p-3"
            >
              <div className="border border-dashed p-3 rounded-lg border-slate-300 flex flex-col">
                {/* JOURNEY HEADER */}
                <div className="flex justify-between items-start">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-5">
                      {/* ORIGIN/FROM */}
                      <div className="text-lg">
                        <span className="font-bold">
                          {
                            airportDetails.find(
                              (each) => each.airportCode === galBooking.origin,
                            )?.placeName
                          }
                        </span>
                      </div>
                      <MoveRight size={24} strokeWidth={1.5} />
                      {/* DESTINATION/TO */}
                      <div className="text-lg">
                        <span className="font-bold">
                          {
                            airportDetails.find(
                              (each) =>
                                each.airportCode === galBooking.destination,
                            )?.placeName
                          }
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-slate-600">
                      {galBooking?.flightBookingSegments.length - 1 === 0
                        ? "Direct flightTicket"
                        : `${galBooking?.flightBookingSegments.length - 1} Stop(s)`}
                    </span>
                  </div>

                  <div className="flex flex-col items-end">
                    <span className="text-xs">
                      <EBadge variant="light" color="blue" size="sm">
                        {humanizeText(galBooking.bookingStatus)}
                      </EBadge>
                    </span>
                    {/* <div className="flex items-center gap-2 mb-1">
                      <div className="text-sm font-semibold">
                        PNR: {trip.pnrDetail?.supplierPnr || "N/A"}
                      </div>
                    </div> */}
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <p>Ticket:</p>
                      <div>
                        {trip.pnrDetail?.ticketNumber
                          ? trip.pnrDetail.ticketNumber
                              .split("/")
                              .map((ticket, index) => (
                                <p key={index}>{ticket}</p>
                              ))
                          : "N/A"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-dashed border-slate-200 flex justify-between items-center">
                  <Badge
                    size="sm"
                    radius="sm"
                    color="blue"
                    variant="light"
                    className="px-2"
                  >
                    <span className="text-gray-600 mr-1">GDS PNR:</span>
                    <span className="">{trip.pnrDetail?.providerPnr}</span>
                  </Badge>
                  <Badge
                    size="sm"
                    radius="sm"
                    color="blue"
                    variant="light"
                    className="px-2"
                  >
                    <span className="text-gray-600 mr-1">Airline PNR:</span>
                    <span className="">{trip.pnrDetail?.supplierPnr}</span>
                  </Badge>
                </div>

                {/* LOCATOR CODES SECTION - for TICKETING_PENDING only */}
                {galBooking.bookingStatus === "TICKETING_PENDING" ? (
                  trip?.locatorCode &&
                  Object.keys(trip.locatorCode).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
                      <EPopover>
                        <Popover.Target>
                          <div className="flex items-center gap-1 cursor-pointer hover:opacity-80">
                            <Tag size={14} className="text-blue-600" />
                            <p className="text-blue-700 font-semibold">
                              View Locator Codes
                            </p>
                          </div>
                        </Popover.Target>

                        <Popover.Dropdown>
                          <div className="p-2">
                            <div className="flex flex-col gap-2">
                              {Object.entries(trip.locatorCode).map(
                                ([key, value]) => (
                                  <div
                                    key={key}
                                    className="flex items-center gap-1.5"
                                  >
                                    <Badge
                                      size="sm"
                                      radius="sm"
                                      color="blue"
                                      variant="light"
                                      className="px-2"
                                    >
                                      <span className="text-gray-600 mr-1">
                                        {humanizeText(key)}:
                                      </span>
                                      <span className="">{value}</span>
                                    </Badge>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </Popover.Dropdown>
                      </EPopover>
                    </div>
                  )
                ) : (
                  <></>
                )}
              </div>

              {galBooking?.flightBookingSegments?.map((segment, i) => (
                <div key={flightTicket.uuid + "__segment_details" + i}>
                  <div>
                    <div className="mt-8">
                      <div className="grid grid-cols-12">
                        {/* ORIGIN/FROM */}
                        <div className="col-span-5">
                          <div className="flex flex-col">
                            <div className="text-lg font-bold">
                              {moment
                                .parseZone(segment.departureTime)
                                .format("HH:mm")}
                            </div>
                            <div className="opacity-70">
                              {moment
                                .parseZone(segment.departureTime)
                                .format("dddd, MMM D")}
                            </div>
                            <div className="font-semibold mt-2">
                              {
                                airportDetails?.find(
                                  (each) => each.airportCode === segment.origin,
                                )?.placeName
                              }{" "}
                              ({segment.origin})
                            </div>
                            <div className="opacity-70 text-sm">
                              {
                                airportDetails?.find(
                                  (each) => each.airportCode === segment.origin,
                                )?.airportName
                              }{" "}
                              {segment?.originTerminal
                                ? `(T${segment?.originTerminal})`
                                : "(N/A)"}
                            </div>
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="h-fit w-fit flex flex-col items-center justify-center gap-2 rounded-full border border-slate-200  py-1 px-2 pr-2.5">
                            <span className="text-sm font-medium text-slate-600 flex items-center gap-1">
                              <Clock size={14} />
                              {getTimeDifference(
                                segment.departureTime,
                                segment.arrivalTime,
                              )}
                            </span>
                            {/* <div className="border-[1.5px] w-full rounded-md border-green-600" /> */}
                          </div>
                        </div>
                        {/* DESTINATION/TO */}
                        <div className="col-span-5 text-right">
                          <div className="flex flex-col items-end w-full">
                            <div className="text-lg font-bold">
                              {moment
                                .parseZone(segment.arrivalTime)
                                .format("HH:mm")}
                            </div>
                            <div className="opacity-70">
                              {moment
                                .parseZone(segment.arrivalTime)
                                .format("dddd, MMM D")}
                            </div>
                            <div className="font-semibold mt-2">
                              {
                                airportDetails?.find(
                                  (each) =>
                                    each.airportCode === segment.destination,
                                )?.placeName
                              }{" "}
                              ({segment.destination})
                            </div>
                            <div className="opacity-70 text-sm">
                              {
                                airportDetails?.find(
                                  (each) =>
                                    each.airportCode === segment.destination,
                                )?.airportName
                              }{" "}
                              {segment?.destinationTerminal
                                ? `(T${segment?.destinationTerminal})`
                                : "(N/A)"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 flex gap-3">
                      <Badge autoContrast color="red" variant="light">
                        {moment
                          .parseZone(segment.departureTime)
                          .format("dddd, MMM D")}
                      </Badge>
                      <Badge autoContrast color="orange" variant="light">
                        {segment.cabin}
                      </Badge>
                      {segment?.cabin ? (
                        <Badge autoContrast color="blue" variant="light">
                          Class: {segment.classOfService}
                        </Badge>
                      ) : (
                        <></>
                      )}
                    </div>

                    {/* carrier, luggage */}
                    <div className="mt-5 flex items-center gap-10">
                      <div className="flex items-center gap-3">
                        <div className="size-11 rounded-md">
                          <img
                            src={getAirLineData(segment.carrier).logo}
                            alt="flightTicket logo"
                            loading="lazy"
                            className="size-full object-cover rounded-lg"
                          />
                        </div>

                        {/* carrier */}
                        <div>
                          <div className="font-semibold">
                            {getAirLineData(segment.carrier).name}
                          </div>
                          <div className="opacity-80">{`${segment.carrier}-${segment.flightNumber}`}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <span>
                          Baggage:{" "}
                          <span className="font-medium">
                            {segment.maxWeight
                              ? `${segment.maxWeight} ${
                                  segment.unit === "Kilograms" ? "kg" : "lbs"
                                }`
                              : "N/A"}
                          </span>
                        </span>
                      </div>

                      {/* <div className="flex items-center gap-1">
                                                            <LuBaggageClaim size={24} />
                                                            <span>Cabin Baggage: <span className="font-semibold">{"7 kg"}</span></span>
                                                        </div> */}

                      <div className="ml-auto">
                        <button
                          className="px-3 py-1.5 text-sm text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors flex items-center gap-1"
                          onClick={() => {
                            mapFareRulesToSegment(
                              trip.id,
                              `${galBooking.origin}_${galBooking.destination}`,
                              segment,
                            );
                            OpenFareRules();
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                          </svg>
                          View Fare Rules
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* <Divider variant="dashed" my="lg" /> */}
                  {galBooking.flightBookingSegments?.length > 1 &&
                  i !== galBooking.flightBookingSegments?.length - 1 ? (
                    <div className="w-full h-9 mt-5 rounded-md border-[1px] border-blue-200 bg-blue-50 text-xs font-semibold flex justify-center items-center gap-2">
                      <TbClockStop size={18} />
                      <span>
                        {getTimeDifference(
                          segment.arrivalTime,
                          galBooking.flightBookingSegments?.[i + 1]
                            .departureTime,
                        )}{" "}
                        Layover
                      </span>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              ))}
            </div>
          )),
        )}

        {/* NAVITAIRE FLIGHT DETAILS */}
        {flightTicket?.bookingResponses?.map((trip) =>
          trip?.navitaireBookingDetailResponses?.map((navBooking) => (
            <div
              key={flightTicket.uuid + "_flight_details"}
              className="mb-5 p-3"
            >
              <div className="border-[1px] border-dashed p-2 rounded-md border-slate-400 flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-5">
                      {/* ORIGIN/FROM */}
                      <div className="text-lg">
                        <span className="font-bold">
                          {
                            airportDetails.find(
                              (each) => each.airportCode === navBooking.origin,
                            )?.placeName
                          }
                        </span>
                      </div>
                      <LuMoveRight size={24} className="text-blue-600" />
                      {/* DESTINATION/TO */}
                      <div className="text-lg">
                        <span className="font-bold">
                          {
                            airportDetails.find(
                              (each) =>
                                each.airportCode === navBooking.destination,
                            )?.placeName
                          }
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-slate-600">
                      {navBooking?.navitaireBookingDetailSegments.length - 1 ===
                      0
                        ? "Direct flightTicket"
                        : `${
                            navBooking?.navitaireBookingDetailSegments.length -
                            1
                          } Stop(s)`}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs">
                      {
                        <EBadge variant="light" color="blue" size="sm">
                          {humanizeText(navBooking.bookingStatus)}
                        </EBadge>
                      }
                    </span>
                    {/* <p className="font-semibold">
                      PNR: {trip.pnrDetail?.supplierPnr}
                    </p> */}
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-dashed border-slate-200 flex justify-between items-center">
                  <Badge
                    size="sm"
                    radius="sm"
                    color="blue"
                    variant="light"
                    className="px-2"
                  >
                    <span className="text-gray-600 mr-1">GDS PNR:</span>
                    <span className="">{trip.pnrDetail?.providerPnr}</span>
                  </Badge>
                  <Badge
                    size="sm"
                    radius="sm"
                    color="blue"
                    variant="light"
                    className="px-2"
                  >
                    <span className="text-gray-600 mr-1">Airline PNR:</span>
                    <span className="">{trip.pnrDetail?.supplierPnr}</span>
                  </Badge>
                </div>
              </div>

              {navBooking?.navitaireBookingDetailSegments.map((segment, i) => (
                <div key={flightTicket + "__segment_details" + i}>
                  <div>
                    <div className="mt-8 w-[100%]">
                      <div className="grid grid-cols-12">
                        <div className="col-span-5">
                          {/* ORIGIN/FROM */}
                          <div className="flex flex-col">
                            <p className="text-lg font-semibold">
                              {moment
                                .parseZone(segment.originDateTime)
                                .format("HH:mm")}
                            </p>
                            <p>
                              {moment
                                .parseZone(segment.originDateTime)
                                .format("dddd, MMM D")}
                            </p>
                            <p className="font-semibold">
                              {
                                airportDetails.find(
                                  (each) => each.airportCode === segment.origin,
                                )?.placeName
                              }{" "}
                              ({segment.origin})
                            </p>
                            <p className="text-navy-blue text-xs">
                              {
                                airportDetails.find(
                                  (each) => each.airportCode === segment.origin,
                                )?.airportName
                              }{" "}
                              {segment?.originTerminal
                                ? `(T${segment?.originTerminal})`
                                : "(N/A)"}
                            </p>
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className="h-full flex flex-col items-center justify-center">
                            <span className="text-xs text-slate-600">
                                {getDuration(segment.originDateTime,segment.arrvLTV, segment.destinationDateTime,segment.deptLTV)}
                            </span>
                            <div className="border-[1.5px] w-full rounded-md border-green-600" />
                          </div>
                        </div>
                        <div className="col-span-5">
                          {/* DESTINATION/TO */}
                          <div className="flex flex-col items-end">
                            <p className="text-lg font-semibold">
                              {moment
                                .parseZone(segment.destinationDateTime)
                                .format("HH:mm")}
                            </p>
                            <p>
                              {moment
                                .parseZone(segment.destinationDateTime)
                                .format("dddd, MMM D")}
                            </p>
                            <p className="font-semibold">
                              {
                                airportDetails.find(
                                  (each) =>
                                    each.airportCode === segment.destination,
                                )?.placeName
                              }{" "}
                              ({segment.destination})
                            </p>
                            <p className="text-navy-blue text-xs">
                              {
                                airportDetails.find(
                                  (each) =>
                                    each.airportCode === segment.destination,
                                )?.airportName
                              }{" "}
                              {segment?.destinationTerminal
                                ? `(T${segment?.destinationTerminal})`
                                : "(N/A)"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 flex gap-3">
                      <Badge autoContrast color="red" variant="light">
                        {moment
                          .parseZone(segment.originDateTime)
                          .format("dddd, MMM D")}
                      </Badge>
                      {/* <Badge autoContrast color="orange" variant="light">{segment.cabin}</Badge> */}
                      {/* {segment?.cabinClass ? <Badge autoContrast color="blue" variant="light">Cabin Class: {segment.cabin}</Badge> : <></>} */}
                    </div>

                    <div className="mt-5 flex items-center gap-12">
                      <div className="h-10 w-10 rounded-md">
                        <img
                          src={getAirLineData(segment.carrierCode).logo}
                          alt="flightTicket logo"
                          loading="lazy"
                          className="h-10 w-10 object-cover rounded-lg"
                        />
                      </div>

                      <div className="flex items-center gap-3">
                        <p className="font-semibold">
                          {getAirLineData(segment.carrierCode).name}
                        </p>
                        <div>{`${segment.carrierCode}-${segment.flightNumber}`}</div>
                      </div>

                      {/* <div className="flex items-center gap-1">
                                                            <LuBaggageClaim size={24} />
                                                            <span>Baggage: <span className="font-semibold">
                                                                {
                                                                    segment.maxWeight
                                                                        ?
                                                                        `${segment.maxWeight} ${segment.unit === "Kilograms" ? "Kg" : "Lbs"}`
                                                                        :
                                                                        "N/A"
                                                                }
                                                            </span></span>
                                                        </div> */}
                    </div>
                  </div>
                  <Divider variant="dashed" my="lg" />
                </div>
              ))}
            </div>
          )),
        )}

        {/* AKBAR FLIGHT DETAILS */}
        {flightTicket?.bookingResponses?.map((trip) =>
          trip?.akbarBookingDetailResponses?.map((akbarJourney, index) => (
            <div
              key={flightTicket.uuid + "_flight_details"}
              className="mb-5 p-3"
            >
              <div className="border-[1px] border-dashed p-2 rounded-md border-slate-400 flex flex-col">
               <div className="flex items-center justify-between">

                <div className="flex items-start flex-col">
                  <div className="flex items-center gap-5">
                    {/* ORIGIN/FROM */}
                    <div className="text-lg">
                      <span className="font-bold">
                        {
                          airportDetails.find(
                            (each) => each.airportCode === akbarJourney.origin,
                          )?.placeName
                        }
                      </span>
                    </div>
                    <LuMoveRight size={24} className="text-blue-600" />
                    {/* DESTINATION/TO */}
                    <div className="text-lg">
                      <span className="font-bold">
                        {
                          airportDetails.find(
                            (each) =>
                              each.airportCode === akbarJourney.destination,
                          )?.placeName
                        }
                      </span>
                    </div>
                  </div>
                  <span className="text-xs text-slate-600">
                    {akbarJourney?.akbarBookingDetailJourneys?.length - 1 === 0
                      ? "Direct flightTicket"
                      : `${
                          akbarJourney?.akbarBookingDetailJourneys?.length - 1
                        } Stop(s)`}
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-xs">
                    {
                      <EBadge variant="light" color="blue" size="sm">
                        {humanizeText(akbarJourney.bookingStatus)}
                      </EBadge>
                    }
                  </span>
                  {(trip.pnrDetail?.ticketNumber && trip.pnrDetail?.ticketNumber !== 'null') && (
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <p>Ticket:</p>
                      <div className="">
                        {trip?.pnrDetail?.ticketNumber?.split("/")[index]}
                      </div>
                    </div>
                  )}
                </div>
               </div>
                <div className="mt-3 pt-3 border-t border-dashed border-slate-200 flex justify-between items-center">
                  <Badge
                    size="sm"
                    radius="sm"
                    color="blue"
                    variant="light"
                    className="px-2"
                  >
                    <span className="text-gray-600 mr-1">GDS PNR:</span>
                    <span className="">{trip.pnrDetail?.providerPnr?.split("|")[0]}</span>
                  </Badge>
                  <Badge
                    size="sm"
                    radius="sm"
                    color="blue"
                    variant="light"
                    className="px-2"
                  >
                    <span className="text-gray-600 mr-1">Airline PNR:</span>
                    <span className="">{trip.pnrDetail?.supplierPnr?.split("|")[0]}</span>
                  </Badge>
                </div>
              </div>
              {akbarJourney?.akbarBookingDetailJourneys?.map(
                (journey, journeyIndex) => {
                  const normalizedSegments = normalizeAkbarJourney(
                    journey,
                    false,
                  );
                  // console.log("Normalized segments: ", normalizedSegments);
                  return normalizedSegments.map((segment, segmentIndex) => (
                    <div
                      key={journeyIndex + "__segment_details_" + segmentIndex}
                    >
                      <div>
                        <div className="mt-8 w-[100%]">
                          <div className="grid grid-cols-12">
                            <div className="col-span-5">
                              {/* ORIGIN/FROM */}
                              <div className="flex flex-col">
                                <p className="text-lg font-semibold">
                                  {moment
                                    .parseZone(segment.departureTime)
                                    .format("HH:mm")}
                                </p>
                                <p className="">
                                  {moment
                                    .parseZone(segment.departureTime)
                                    .format("dddd, MMM D")}
                                </p>

                                <p className="font-semibold">
                                  {
                                    airportDetails.find(
                                      (each) =>
                                        each.airportCode === segment.origin,
                                    )?.placeName
                                  }{" "}
                                  ({segment.origin})
                                </p>
                                <p className="text-navy-blue text-xs">
                                  {
                                    airportDetails.find(
                                      (each) =>
                                        each.airportCode === segment.origin,
                                    )?.airportName
                                  }{" "}
                                  {segment?.originTerminal
                                    ? `(T${segment?.originTerminal})`
                                    : "(N/A)"}
                                </p>
                              </div>
                            </div>
                            {/*----- Duration---- */}
                            <div className="col-span-2">
                              <div className="h-full flex items-center justify-center">
                                <div className="flex flex-col items-center gap-1">
                                  <span className="text-xs text-slate-600">
                                    {segment.duration}
                                  </span>
                                  <div className="border-[1.5px] w-full rounded-md border-green-600" />
                                </div>
                              </div>
                            </div>
                            <div className="col-span-5">
                              {/* DESTINATION/TO */}
                              <div className="flex flex-col items-end">
                                <p className="text-lg font-semibold">
                                  {moment
                                    .parseZone(segment.arrivalTime)
                                    .format("HH:mm")}
                                </p>
                                <p className="">
                                  {moment
                                    .parseZone(segment.arrivalTime)
                                    .format("dddd, MMM D")}
                                </p>
                                <p className="font-semibold">
                                  {
                                    airportDetails.find(
                                      (each) =>
                                        each.airportCode ===
                                        segment.destination,
                                    )?.placeName
                                  }{" "}
                                  ({segment.destination})
                                </p>
                                <p className="text-navy-blue text-xs">
                                  {
                                    airportDetails.find(
                                      (each) =>
                                        each.airportCode ===
                                        segment.destination,
                                    )?.airportName
                                  }{" "}
                                  {segment?.destinationTerminal
                                    ? `(T${segment?.destinationTerminal})`
                                    : "(N/A)"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-8 flex gap-3">
                          <Badge autoContrast color="red" variant="light">
                            {moment
                              .parseZone(segment.departureTime)
                              .format("dddd, MMM D")}
                          </Badge>
                          <Badge autoContrast color="orange" variant="light">
                            {segment.cabin}
                          </Badge>
                          {segment?.fareClass ? (
                            <Badge autoContrast color="blue" variant="light">
                              Fare Class: {segment.fareClass}
                            </Badge>
                          ) : (
                            <></>
                          )}
                        </div>

                        <div className="mt-5 flex items-center gap-12">
                          <div className="h-10 w-10 rounded-md">
                            <img
                              src={getAirLineData(segment.carrier).logo}
                              alt="flightTicket logo"
                              loading="lazy"
                              className="h-10 w-10 object-cover rounded-lg"
                            />
                          </div>

                          <div className="flex items-center gap-3">
                            <p className="font-semibold">
                              {getAirLineData(segment.carrier).name}
                            </p>
                            <div>{`${segment.carrier}-${segment.flightNumber}`}</div>
                          </div>

                          <div className="flex items-center gap-1">
                            <span>
                              Baggage:{" "}
                              <span className="font-semibold">
                                {segment.baggage}
                              </span>
                            </span>
                          </div>

                          {segment?.aircraft && (
                            <div className="flex items-center gap-1">
                              <span>
                                Aircraft:{" "}
                                <span className="font-semibold">
                                  {segment.aircraft}
                                </span>
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* SEGMENT WISE PAX_SSR MAPPING */}
                      {(segmentIndex === normalizedSegments.length - 1) && (
                      <ETable>
                        <thead>
                          <tr>
                            <th>S.No.</th>
                            <th>Passenger</th>
                            <th>Service Purchased</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trip?.akbarPassengerDetailResponses?.map(
                            (eachPassenger, index) => (
                              <tr key={`passenger-${index}`}>
                                <td>{index + 1}</td>
                                <td className="font-medium">
                                  <span>
                                    {eachPassenger?.title}{" "}
                                    {eachPassenger?.firstName}{" "}
                                    {eachPassenger?.lastName}
                                  </span>
                                  <span>({eachPassenger?.gender})</span>
                                </td>
                                <td>
                                  <AkbarSsrDetails
                                    journey={journey}
                                    passenger={eachPassenger}
                                  />
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </ETable>
                      )}

                      {normalizedSegments.length > 1 &&
                      segmentIndex !== normalizedSegments.length - 1 ? (
                        <div className="w-full h-9 mt-5 mb-3 rounded-md border-[1px] border-blue-200 bg-blue-50 text-xs font-semibold flex justify-center items-center gap-2">
                          <TbClockStop size={18} />
                          <span>
                            {getTimeDifference(
                              segment.arrivalTime,
                              normalizedSegments[segmentIndex + 1]
                                .departureTime,
                            )}{" "}
                            Layover
                          </span>
                        </div>
                      ) : (
                        <Divider variant="dashed" my="lg" />
                      )}
                    </div>
                  ));
                },
              )}
            </div>
          )),
        )}
      </div>

      {/* GALILEO FARE RULES (CANCELLATION & DATE CHANGE) */}
      <EModal
        opened={showFareRules}
        onClose={closeFareRules}
        size={"80%"}
        title={
          <div className="text-xl font-semibold text-navy-blue">
            Fare Rules & Policies
          </div>
        }
        className="overflow-hidden"
      >
        <div className="grid grid-cols-2 gap-8">
          <div>
            <Tabs
              color="teal"
              defaultValue="cancellation_charges"
              styles={{
                tab: { fontWeight: 500 },
              }}
            >
              <Tabs.List>
                <Tabs.Tab value="cancellation_charges">
                  Cancellation Charges
                </Tabs.Tab>
                <Tabs.Tab value="date_change_charges" color="blue">
                  Date Change Charges
                </Tabs.Tab>
              </Tabs.List>

              {/* CANCELLATION CHARGES */}
              <Tabs.Panel value="cancellation_charges" pt="md">
                {isMyChargeLoading ? (
                  <div className="flex flex-col gap-3">
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                  </div>
                ) : (
                  <Table withTableBorder highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>
                          <div className="flex flex-col">
                            <p className="text-sm">Time frame</p>
                            <p className="text-xs text-gray-500">
                              (From Scheduled flight departure)
                            </p>
                          </div>
                        </Table.Th>
                        <Table.Th>
                          <div className="flex flex-col">
                            <p className="text-sm">Service Fee</p>
                            <p className="text-xs text-gray-500">
                              (Per passenger)
                            </p>
                          </div>
                        </Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      <Table.Tr>
                        <Table.Td>Anytime (For Domestic)</Table.Td>
                        <Table.Td>
                          <NumberFormatter
                            thousandSeparator
                            prefix="₹ "
                            value={
                              myCharges.customerCancellationDomesticCharge ?? 0
                            }
                          />
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td>Anytime (For International)</Table.Td>
                        <Table.Td>
                          <NumberFormatter
                            thousandSeparator
                            prefix="₹ "
                            value={
                              myCharges.customerCancellationInternationalCharge ??
                              0
                            }
                          />
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                )}

                <div className="p-4 my-5 text-sm font-semibold flex justify-center items-center bg-red-50 text-red-600 rounded-md">
                  ROE conversion rate can be charged.
                </div>

                {/* GST & RAF CHARGES */}
                <div className="border border-blue-200 bg-blue-50 rounded-md p-4">
                  <div className="flex flex-col gap-3">
                    <p className="font-semibold text-navy-blue">
                      GST and RAF charges will apply to any cancellation
                      penalties.
                    </p>
                    <div className="flex items-start gap-2 text-gray-700">
                      <span>•</span>
                      <p>
                        The information provided is for reference only. Airline
                        fare rules are subject to change, and cancellation fees
                        may vary based on fare class and currency exchange rate
                        fluctuations.
                      </p>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700">
                      <span>•</span>
                      <p>
                        Cancellation and reissue fees will be determined by the
                        most restrictive fare type.
                      </p>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700">
                      <span>•</span>
                      <p>
                        For the exact cancellation or change fee, please feel
                        free to contact our support team.
                      </p>
                    </div>
                  </div>
                </div>
              </Tabs.Panel>

              {/* DATE CHANGE CHARGES */}
              <Tabs.Panel value="date_change_charges" pt="md">
                {isMyChargeLoading ? (
                  <div className="flex flex-col gap-3">
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                    <Skeleton height={40} />
                  </div>
                ) : (
                  <Table withTableBorder highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>
                          <div className="flex flex-col">
                            <p className="text-sm">Time frame</p>
                            <p className="text-xs text-gray-500">
                              (From Scheduled flight departure)
                            </p>
                          </div>
                        </Table.Th>
                        <Table.Th>
                          <div className="flex flex-col">
                            <p className="text-sm">Service Fee</p>
                            <p className="text-xs text-gray-500">
                              (Per passenger)
                            </p>
                          </div>
                        </Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      <Table.Tr>
                        <Table.Td>Anytime (For Domestic)</Table.Td>
                        <Table.Td>
                          <NumberFormatter
                            thousandSeparator
                            prefix="₹ "
                            value={
                              myCharges.customerModificationDomesticCharge ?? 0
                            }
                          />
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Td>Anytime (For International)</Table.Td>
                        <Table.Td>
                          <NumberFormatter
                            thousandSeparator
                            prefix="₹ "
                            value={
                              myCharges.customerModificationInternationalCharge ??
                              0
                            }
                          />
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                )}

                <div className="p-4 my-5 text-sm font-semibold flex justify-center items-center bg-red-50 text-red-600 rounded-md">
                  ROE conversion rate can be charged.
                </div>

                {/* GST CHARGES */}
                <div className="border border-blue-200 bg-blue-50 rounded-md p-4">
                  <div className="flex flex-col gap-3">
                    <p className="font-semibold text-navy-blue">
                      GST + Any fare difference will be applicable extra.
                    </p>
                  </div>
                </div>
              </Tabs.Panel>
            </Tabs>
          </div>

          <div className="overflow-auto max-h-[80vh]">
            {/* GALILEO FARE RULES */}
            <div className="w-[97%] border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="text-sm font-semibold mb-3 text-navy-blue border-b pb-2">
                Original Airline Fare Rules
              </div>
              <ScrollArea h={"90%"} scrollbarSize={8}>
                {selectedFareRules?.[Object.keys(selectedFareRules)?.[0]]?.map(
                  (eachRule, index) => (
                    <React.Fragment key={index + "_1G_fare_rule"}>
                      <EHighlightedText
                        className="text-sm font-mono bg-white p-3 rounded-md mb-2 block"
                        text={eachRule?.value?.trim()}
                      />
                    </React.Fragment>
                  ),
                )}
              </ScrollArea>
            </div>
          </div>
        </div>
      </EModal>

    </div>
  );
};

export default FlightDetails;
